import { useEffect, useState } from 'react';

export type AppearancePreference = 'system' | 'light' | 'dark';

const APPEARANCE_STORAGE_KEY = 'slider:appearance:v1';
const systemDarkQuery = '(prefers-color-scheme: dark)';

function isAppearancePreference(value: string | null): value is AppearancePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

function getAppearancePreference(): AppearancePreference {
  const stored = globalThis.localStorage?.getItem(APPEARANCE_STORAGE_KEY) ?? null;
  // New browsers start in light; System remains an explicit preference.
  return isAppearancePreference(stored) ? stored : 'light';
}

function applyAppearance(preference: AppearancePreference): void {
  // While the opt-in management Appearance Studio owns document theme state,
  // do not let the older Light/Dark/System picker overwrite its staged preview.
  if (document.documentElement.hasAttribute('data-manage-appearance-active')) return;
  const dark =
    preference === 'dark' ||
    (preference === 'system' && globalThis.matchMedia?.(systemDarkQuery).matches === true);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

function setAppearancePreference(preference: AppearancePreference): void {
  localStorage.setItem(APPEARANCE_STORAGE_KEY, preference);
  applyAppearance(preference);
  globalThis.dispatchEvent(new CustomEvent('slider:appearance-change', { detail: preference }));
}

export function useAppearance(): readonly [AppearancePreference, typeof setAppearancePreference] {
  const [preference, setPreference] = useState<AppearancePreference>(getAppearancePreference);

  useEffect(() => {
    const media = globalThis.matchMedia?.(systemDarkQuery);
    const sync = () => {
      const next = getAppearancePreference();
      setPreference(next);
      applyAppearance(next);
    };
    media?.addEventListener('change', sync);
    globalThis.addEventListener('slider:appearance-change', sync);
    sync();
    return () => {
      media?.removeEventListener('change', sync);
      globalThis.removeEventListener('slider:appearance-change', sync);
    };
  }, []);

  return [preference, setAppearancePreference] as const;
}
